import React from "react";
import { IndianRupee, CalendarDays, Clock } from "lucide-react";

function daysBetween(a, b) {
  try { return Math.round((new Date(b) - new Date(a)) / (1000 * 60 * 60 * 24)); } catch { return null; }
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); } catch { return iso; }
}

function fmtINR(n) {
  if (n == null || n === "") return "—";
  return `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

export default function ContractSummary({ client }) {
  if (!client) return null;
  const cv = client.contract_value;
  const es = client.engagement_start;
  const ee = client.engagement_end;
  const payments = client.payments || [];
  const hasAnything = cv != null || es || ee || payments.length > 0;
  if (!hasAnything) return null;

  const today = new Date().toISOString().slice(0, 10);
  const daysLeft = ee ? daysBetween(today, ee) : null;
  const daysEngaged = es ? daysBetween(es, today) : null;
  const totalDays = (es && ee) ? daysBetween(es, ee) : null;
  const engagementCls = daysLeft == null ? "text-zinc-500"
    : daysLeft < 0 ? "text-[#EF4444]"
    : daysLeft < 7 ? "text-[#EF4444]"
    : daysLeft < 30 ? "text-amber-400"
    : "text-emerald-400";

  // Aggregate payments
  const paid = payments.filter((p) => p.paid);
  const unpaid = payments.filter((p) => !p.paid);
  const paidTotal = paid.reduce((s, p) => s + Number(p.amount || 0) * (1 + Number(p.tax_percent || 0) / 100), 0);
  const unpaidTotal = unpaid.reduce((s, p) => s + Number(p.amount || 0) * (1 + Number(p.tax_percent || 0) / 100), 0);
  const nextUnpaid = [...unpaid].sort((a, b) => (a.date || "").localeCompare(b.date || ""))[0];

  return (
    <div className="tt-card p-4 sm:p-5 mb-5" data-testid="contract-summary">
      <div className="flex items-center justify-between mb-4">
        <div className="t-eyebrow flex items-center gap-2">
          <IndianRupee className="w-3.5 h-3.5" /> Contract
        </div>
        {daysLeft != null && (
          <div className={`text-[11px] font-mono ${engagementCls}`}>
            {daysLeft < 0 ? `Engagement ended ${Math.abs(daysLeft)}d ago` : `${daysLeft}d of engagement left`}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Overall value</div>
          <div className="font-display text-[22px] text-zinc-100 mt-0.5">{fmtINR(cv)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Paid so far</div>
          <div className="font-display text-[22px] text-emerald-400 mt-0.5">{fmtINR(paidTotal)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Outstanding</div>
          <div className={`font-display text-[22px] mt-0.5 ${unpaidTotal > 0 ? "text-amber-400" : "text-zinc-500"}`}>{fmtINR(unpaidTotal)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Next payment</div>
          <div className="font-display text-[16px] text-zinc-100 mt-0.5" data-testid="contract-next-payment">
            {nextUnpaid ? `${fmtDate(nextUnpaid.date)}` : "—"}
          </div>
          {nextUnpaid && <div className="text-[10px] text-zinc-500 font-mono mt-0.5">{fmtINR(Number(nextUnpaid.amount || 0) * (1 + Number(nextUnpaid.tax_percent || 0) / 100))}</div>}
        </div>
      </div>

      {(es || ee) && (
        <div className="border-t border-zinc-900 pt-3 mb-3 flex items-center justify-between text-[11px] font-mono text-zinc-500">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-3 h-3" />
            <span>{fmtDate(es)} → {fmtDate(ee)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-3 h-3" />
            <span>{daysEngaged != null && totalDays != null ? `${daysEngaged} / ${totalDays} days` : (daysEngaged != null ? `${daysEngaged} days engaged` : "")}</span>
          </div>
        </div>
      )}

      {payments.length > 0 && (
        <div className="border-t border-zinc-900 pt-3">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-2">Payment schedule</div>
          <div className="grid grid-cols-1 gap-1">
            {[...payments].sort((a, b) => (a.date || "").localeCompare(b.date || "")).map((p) => {
              const incl = Number(p.amount || 0) * (1 + Number(p.tax_percent || 0) / 100);
              return (
                <div key={p.id} className="flex items-center gap-3 px-2 py-1.5 text-[12px]" data-testid={`contract-payment-${p.id}`}>
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${p.paid ? "bg-emerald-400" : "bg-amber-400"}`} />
                  <span className="w-24 text-zinc-400 font-mono text-[11px]">{fmtDate(p.date)}</span>
                  <span className="flex-1 truncate text-zinc-200">{p.note || "Payment"}</span>
                  <span className={p.paid ? "text-emerald-400 font-mono text-[11px]" : "text-zinc-200 font-mono text-[11px]"}>
                    {fmtINR(incl)}
                  </span>
                  <span className="w-14 text-right text-[10px] uppercase tracking-wider font-mono">
                    {p.paid ? <span className="text-emerald-500">paid</span> : <span className="text-amber-400">due</span>}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
